import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * "Up next" at the end of an episode: shows the next episode with a countdown, then plays it.
 * OK / "Play now" starts it straight away; Back or "Cancel" stays on the finished episode.
 * While it is up it owns the remote through window.__zflixTopKeys (tvSpatialNav hands every key there first).
 * After STILL_WATCHING_AFTER episodes started on their own with no button pressed, it asks "Are you still watching?"
 * instead and waits, so a show does not stream all night to someone asleep.
 */
const COUNTDOWN_S = 10;
export const STILL_WATCHING_AFTER = 3;

/**
 * Episodes started by the countdown in a row; anyone touching the remote or the screen starts the count over.
 * The TV's key handler stops key events before other listeners see them, so the player reports remote presses
 * through noteViewerActive(); taps, clicks and the TV app's OK (a plain click) arrive here.
 */
const autoRun = { count: 0 };
export function noteViewerActive() {
  autoRun.count = 0;
}
if (typeof window !== 'undefined') {
  for (const type of ['keydown', 'pointerdown', 'touchstart', 'click']) window.addEventListener(type, noteViewerActive, true);
}
export function stillWatchingDue() {
  return autoRun.count >= STILL_WATCHING_AFTER;
}

export function fetchNextEpisode(episodeId) {
  return fetch(`/api/shows/next-episode?episodeId=${encodeURIComponent(episodeId)}`, { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : { next: null }))
    .then((j) => (j && j.next && j.next.id ? j.next : null))
    .catch(() => null);
}

export function UpNextOverlay({ next, onPlay, onCancel }) {
  /** Decided once when the card opens: a key pressed to answer it must not turn it back into a countdown. */
  const [ask] = useState(stillWatchingDue);
  const [left, setLeft] = useState(COUNTDOWN_S);
  const [pick, setPick] = useState(0);
  const pickRef = useRef(0);
  pickRef.current = pick;
  const doneRef = useRef(false);
  const playRef = useRef(null);
  const cancelRef = useRef(null);

  const finish = useCallback(
    (play, auto = false) => {
      if (doneRef.current) return;
      doneRef.current = true;
      if (play) onPlay(next);
      else onCancel();
      /** A choice made on the card is someone watching; only the countdown's own start counts toward asking. */
      if (auto) autoRun.count += 1;
      else autoRun.count = 0;
    },
    [next, onPlay, onCancel],
  );

  useEffect(() => {
    if (ask) return undefined;
    const iv = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(iv);
  }, [ask]);
  useEffect(() => {
    if (!ask && left <= 0) finish(true, true);
  }, [ask, left, finish]);

  useEffect(() => {
    const before = document.activeElement;
    window.__zflixTopKeys = (e) => {
      const k = e.key;
      const code = e.keyCode;
      if (k === 'ArrowLeft' || code === 37 || k === 'ArrowRight' || code === 39) setPick((p) => (p ? 0 : 1));
      else if (k === 'Enter' || code === 13) finish(pickRef.current === 0);
      else if (k === 'Escape' || k === 'GoBack' || k === 'BrowserBack') finish(false);
      return true;
    };
    const prevBack = window.__zflixOwnBack;
    window.__zflixOwnBack = () => {
      finish(false);
      return true;
    };
    return () => {
      delete window.__zflixTopKeys;
      if (prevBack) window.__zflixOwnBack = prevBack;
      else delete window.__zflixOwnBack;
      try {
        if (before && document.contains(before) && typeof before.focus === 'function') before.focus({ preventScroll: true });
      } catch (_) {}
    };
  }, [finish]);

  /** The TV app delivers OK as a click on whatever has focus, so the chosen button holds real focus. */
  useEffect(() => {
    try {
      (pick === 0 ? playRef.current : cancelRef.current)?.focus({ preventScroll: true });
    } catch (_) {}
  }, [pick]);

  const label =
    (next.seasonIndex != null ? `S${next.seasonIndex}` : '') + (next.episodeIndex != null ? ` E${next.episodeIndex}` : '');
  const btn = (on) => ({
    padding: '12px 22px',
    fontSize: 18,
    fontWeight: 700,
    borderRadius: 10,
    border: on ? '3px solid #fff' : '3px solid transparent',
    background: on ? 'var(--accent)' : 'rgba(255,255,255,0.14)',
    color: '#fff',
    cursor: 'pointer',
  });
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', right: 32, bottom: 40, zIndex: 100000, width: 'min(520px, calc(100vw - 32px))', background: 'rgba(12,12,16,0.94)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 14, padding: '18px 20px', color: '#fff', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}
    >
      {ask ? (
        <div data-zflix-still-watching style={{ fontSize: 22, fontWeight: 700 }}>Are you still watching?</div>
      ) : (
        <div style={{ fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text2)' }}>Up next in {Math.max(0, left)}</div>
      )}
      <div style={{ fontSize: ask ? 16 : 22, fontWeight: ask ? 400 : 700, marginTop: 6, color: ask ? 'var(--text2)' : '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ask ? 'Next: ' : ''}{label.trim()}{next.name ? `${label.trim() ? ' · ' : ''}${next.name}` : ''}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
        <button ref={playRef} type="button" style={btn(pick === 0)} onClick={(e) => { e.stopPropagation(); finish(true); }}>{ask ? 'Keep watching' : 'Play now'}</button>
        <button ref={cancelRef} type="button" style={btn(pick === 1)} onClick={(e) => { e.stopPropagation(); finish(false); }}>{ask ? 'Stop' : 'Cancel'}</button>
      </div>
    </div>
  );
}
